import React from 'react';
import { BaseEditModal } from '../base-edit-modal';
import styles from './styles.module.scss';
import { Input } from '../../form';
import { CustomSelect } from '../../form/select';
import type { OptionType } from '../../form/select/util';
import { CircularPlusButton } from '../../add-multiple-questions';
import { useForm, useFieldArray, useWatch, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { TableSelectIcon } from 'assets';

const ItemTypes = {
  DECISION: 'decision',
};

const schema = yup.object().shape({
  decisions: yup.array().of(
    yup.object().shape({
      decision: yup.string().required('Decision is required'),
      rangeStart: yup
        .number()
        .required('Start range is required')
        .max(yup.ref('rangeEnd'), 'Start must be less than or equal to end')
        .min(0, 'Start must be at least 0')
        .max(100, 'Start must be at most 100'),
      rangeEnd: yup
        .number()
        .required('End range is required')
        .min(0, 'End must be at least 0')
        .max(100, 'End must be at most 100'),
    })
  )
    .min(1, 'At least one decision is required')
    .test(
      'non-overlapping-ranges',
      'Decision bucket ranges cannot overlap. Adjust the ranges before saving.',
      (decisions = []) => {
        const sortedDecisions = [...decisions].sort((a, b) => a.rangeStart - b.rangeStart);

        for (let index = 1; index < sortedDecisions.length; index += 1) {
          if (sortedDecisions[index].rangeStart <= sortedDecisions[index - 1].rangeEnd) {
            return false;
          }
        }

        return true;
      }
    ),
});

interface DecisionBucketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { decisions: { decision: string; rangeStart: number; rangeEnd: number }[] }) => void;
  isLoading?: boolean;
  currentData?: { decisions: { decision: string; rangeStart: number; rangeEnd: number }[] };
}

interface DragItemProps {
  index: number;
  moveDecision: (fromIndex: number, toIndex: number) => void;
  field: any;
  register: any;
  errors: any;
  control: any;
  isLoading?: boolean;
  remove: (index: number) => void;
  rangeOptions: OptionType[];
  getOptionByValue: (value: number) => OptionType;
  overlapMessage?: string;
}

const DragItem: React.FC<DragItemProps> = ({
  index,
  moveDecision,
  field,
  register,
  errors,
  control,
  isLoading,
  remove,
  rangeOptions,
  getOptionByValue,
  overlapMessage,
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.DECISION,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.DECISION,
    hover: (item: { index: number }) => {
      if (item.index !== index) {
        moveDecision(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={styles.formGroup}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className={styles.decision}>
        <div className={styles.dragHandle}>
          <TableSelectIcon className={styles.dragIcon} />
        </div>
        <Input
          label="Decision"
          placeholder="Enter decision"
          name={`decisions[${index}].decision`}
          register={register}
          validatorMessage={errors.decisions?.[index]?.decision?.message}
          disabled={isLoading}
          parentClassName={styles.inputWrapper}
        />
      </div>

      <div className={styles.preferenceRow}>
        <Controller
          name={`decisions.${index}.rangeStart`}
          control={control}
          render={({ field }) => (
            <CustomSelect
              label="Range"
              placeholder="Select start"
              options={rangeOptions}
              name={`decisions.${index}.rangeStart`}
              value={getOptionByValue(field.value)}
              onChange={(option) => field.onChange(parseInt(option?.value || '0'))}
              validatorMessage={errors.decisions?.[index]?.rangeStart?.message ?? ''}
              disabled={isLoading}
              inputClass={styles.selectInput}
              parentClassName={styles.selectWrapper}
            />
          )}
        />
        <span className={styles.rangeSeparator}>–</span>
        <Controller
          name={`decisions.${index}.rangeEnd`}
          control={control}
          render={({ field }) => (
            <CustomSelect
              placeholder="Select end"
              options={rangeOptions}
              name={`decisions.${index}.rangeEnd`}
              value={getOptionByValue(field.value)}
              onChange={(option) => field.onChange(parseInt(option?.value || '0'))}
              validatorMessage={errors.decisions?.[index]?.rangeEnd?.message ?? ''}
              disabled={isLoading}
              inputClass={styles.selectInput}
              parentClassName={styles.selectWrapper}
            />
          )}
        />
      </div>
      {overlapMessage && (
        <small className={`${styles.message} ${styles.overlapMessage}`}>{overlapMessage}</small>
      )}
      {index > 2 && (
        <button
          type="button"
          onClick={() => remove(index)}
          className={styles.removeButton}
          disabled={isLoading}
        >
          Remove
        </button>
      )}
      {errors.decisions?.[index] && (
        <small className={styles.message}>
          {errors.decisions?.[index]?.message || 'Please ensure all fields are valid'}
        </small>
      )}
    </div>
  );
};

export const DecisionBucketModal: React.FC<DecisionBucketModalProps> = ({
  isOpen,
  onClose,
  onSave,
  isLoading,
  currentData,
}) => {
  const defaultValues = {
    decisions: currentData?.decisions || [{ decision: '', rangeStart: 0, rangeEnd: 0 }],
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema) as Resolver<{decisions: any[]}>,
    defaultValues,
    mode: 'onChange',
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'decisions',
  });

  // Live values so overlap feedback updates in real time as the user edits,
  // pinpointing the specific row that clashes instead of only a single
  // summary message at submit time.
  //
  // useWatch (the hook) — not watch() — is required here: each range dropdown
  // is rendered inside an isolated Controller, so changing one only re-renders
  // that Controller. useWatch subscribes THIS component so it re-renders on any
  // decision change and the overlap check below recomputes against fresh values.
  const watchedDecisions = useWatch({ control, name: 'decisions' });
  const overlapMessages = React.useMemo(() => {
    const messages: Record<number, string> = {};
    const list = watchedDecisions ?? [];

    for (let i = 0; i < list.length; i += 1) {
      const a = list[i];
      const aStart = Number(a?.rangeStart);
      const aEnd = Number(a?.rangeEnd);
      if (Number.isNaN(aStart) || Number.isNaN(aEnd)) continue;

      for (let j = 0; j < list.length; j += 1) {
        if (i === j) continue;
        const b = list[j];
        const bStart = Number(b?.rangeStart);
        const bEnd = Number(b?.rangeEnd);
        if (Number.isNaN(bStart) || Number.isNaN(bEnd)) continue;

        // Two ranges overlap when each starts on or before the other ends.
        if (aStart <= bEnd && bStart <= aEnd) {
          const otherName = (b?.decision ?? '').trim();
          messages[i] = otherName
            ? `Range overlaps with “${otherName}” (${bStart}–${bEnd}). Adjust the range.`
            : `Range overlaps with another decision (${bStart}–${bEnd}). Adjust the range.`;
          break;
        }
      }
    }

    return messages;
  }, [watchedDecisions]);

  // Load current data into the form when the modal opens. We intentionally do
  // NOT depend on `currentData`: the parent rebuilds it (a new object) on every
  // render, so depending on it would reset the form on any incidental parent
  // re-render and wipe the user's in-progress edits (and any overlap warning).
  const wasOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      reset(defaultValues);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, reset]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = (data: { decisions: { decision: string; rangeStart: number; rangeEnd: number }[] | undefined }) => {
    onSave({ decisions: data.decisions ?? [] });
    onClose();
  };

  const handleAddDecision = () => {
    append({ decision: '', rangeStart: 0, rangeEnd: 0 });
  };

  const rangeOptions: OptionType[] = Array.from({ length: 101 }, (_, i) => {
    const num = 100 - i;
    return { value: num.toString(), label: num.toString() };
  });

  const getOptionByValue = (value: number): OptionType => {
    return rangeOptions.find((opt) => opt.value === value.toString()) || { value: '', label: 'Select' };
  };

  return (
    <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true, delayTouchStart: 150 }}>
      <BaseEditModal
        isOpen={isOpen}
        onClose={onClose}
        title="Decision Bucket"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles.formSection}>
          <div className={styles.categorySection}>
            {(errors.decisions?.root?.message || errors.decisions?.message) && (
              <small className={styles.globalMessage}>
                {errors.decisions?.root?.message || errors.decisions?.message}
              </small>
            )}
            {fields.map((field, index) => (
              <DragItem
                key={field.id}
                index={index}
                moveDecision={move}
                field={field}
                register={register}
                errors={errors}
                control={control}
                isLoading={isLoading}
                remove={remove}
                rangeOptions={rangeOptions}
                getOptionByValue={getOptionByValue}
                overlapMessage={overlapMessages[index]}
              />
            ))}
            <button
              type="button"
              onClick={handleAddDecision}
              className={styles.addButton}
              disabled={isLoading}
            >
              <CircularPlusButton onClick={() => {}} className={styles.plusButton} /> Add more Decision
            </button>
          </div>
        </div>
      </BaseEditModal>
    </DndProvider>
  );
};
